import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, X, ChevronLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { isBrokerPersona } from '../../constants/personas';
import DirectMessagePanel from './DirectMessagePanel';
import DealConversationDealLink from './DealConversationDealLink';
import {
  ensureDirectConversation,
  subscribeDirectConversationsForUser,
  DIRECT_CONVERSATIONS_INBOX_LIMIT,
  buildBrokerClientConversationSpec,
  buildLenderFundInvestorConversationSpec,
  buildShareRecipientConversationSpec,
  findShareTokenForDirectChat,
  parseShareTokenFromShareRecipientConversationId,
  resolveShareRecipientDirectMeta,
} from '../../services/directChatService';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { getSharesByProperty, getShare } from '../../services/shareService';
import { subscribeAggregatedShareParticipants } from '../../services/shareParticipantsService';
import { addBrokerLink } from '../../services/brokerLinksService';
import { getBrokerDisplayName } from '../../services/brokerDisplayNameService';
import { subscribeFundInvestorsByLender } from '../../services/fundInvestorsService';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import {
  FLOATING_DOCK_EVENT,
  DOCK_PANEL_WIDTH,
  DOCK_PANEL_HEIGHT,
  DOCK_PANEL_RIGHT,
  DOCK_PANEL_BOTTOM,
  DOCK_RIGHT,
  DOCK_BOTTOM,
  DOCK_FAB_BUTTON,
  FLOATING_DOCK_CLASS,
  announceDockOpen,
} from '../../utils/floatingDocks';

function peerLabelFromUid(uid, t) {
  if (!uid) return t('directChat.peerFallback');
  return `${uid.slice(0, 10)}…`;
}

function directMessageTsToMs(v) {
  if (!v) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return 0;
}

/** Last message in this conversation is from the other participant and newer than fab last-seen. */
function directConversationUnreadFromPeer(row, currentUid, fabLastSeenMs) {
  if (!row || !currentUid) return false;
  const author = row.lastMessageAuthorUid;
  if (!author || author === currentUid) return false;
  return directMessageTsToMs(row.lastMessageAt) > fabLastSeenMs;
}

function fabLastSeenStorageKey(uid) {
  return `directChatFabLastSeenAt:${uid}`;
}

function ThreadPane({ thread, currentUser, onBack, t, currentStep, currentProperty, onOpenDealFromChat }) {
  const { conversationId, ensureSpec, title, subtitle, directConvMeta: threadMeta } = thread;
  const directConvMeta = useMemo(
    () => resolveShareRecipientDirectMeta(conversationId, threadMeta, ensureSpec),
    [conversationId, threadMeta, ensureSpec],
  );
  const [ready, setReady] = useState(!ensureSpec);
  const [ensureErrKind, setEnsureErrKind] = useState(null);

  useEffect(() => {
    if (!ensureSpec) {
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    setReady(false);
    setEnsureErrKind(null);
    ensureDirectConversation(conversationId, ensureSpec)
      .catch((e) => {
        if (!cancelled) {
          console.warn('[directChat] ensureDirectConversation failed', conversationId, e?.code, e?.message);
          setEnsureErrKind(e?.code === 'DIRECT_CHAT_BROKER_CLIENT' ? 'brokerClient' : 'generic');
        }
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, ensureSpec]);

  return (
    <div className="flex flex-col min-h-0 flex-1 bg-white">
      <div className="flex items-center gap-2 px-2 py-2 border-b border-slate-200 shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
          aria-label={t('floatingChat.back')}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 truncate">{title}</p>
          {subtitle ? <p className="text-xs text-slate-500 truncate">{subtitle}</p> : null}
          <DealConversationDealLink
            conversationId={conversationId}
            directConvMeta={directConvMeta}
            currentUser={currentUser}
            currentStep={currentStep}
            currentProperty={currentProperty}
            onOpenDealFromChat={onOpenDealFromChat}
            t={t}
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {!ready ? (
          <p className="text-sm text-slate-500 p-4">{t('loading')}</p>
        ) : ensureErrKind ? (
          <p className="text-sm text-red-600 p-4">
            {ensureErrKind === 'brokerClient'
              ? t('directChat.ensureErrorBrokerClient')
              : t('directChat.ensureError')}
          </p>
        ) : (
          <DirectMessagePanel
            conversationId={conversationId}
            currentUser={currentUser}
            subtitle={subtitle}
            className="border-0 rounded-none shadow-none flex-1 min-h-0"
            flexibleHeight
            hideTitleBar
          />
        )}
      </div>
    </div>
  );
}

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (v: boolean) => void,
 *   currentStep: string,
 *   currentProperty: { id?: string, uid?: string } | null,
 *   clients?: Array<{ id: string, clientUserId?: string, name?: string, email?: string }>,
 *   onOpenDealFromChat?: (payload: { type: 'owned', propertyId: string } | { type: 'shared', shareToken: string }) => void,
 * }} props
 */
export default function FloatingDirectChatDock({
  open,
  onOpenChange,
  currentStep,
  currentProperty,
  clients = [],
  onOpenDealFromChat,
}) {
  const { currentUser, userProfile, linkedBrokers, fundInvestorsAsInvestor } = useAuth();
  const { t } = useLanguage();
  const [view, setView] = useState('hub');
  const [thread, setThread] = useState(null);
  const [convRows, setConvRows] = useState([]);
  const [convErr, setConvErr] = useState(false);
  const [lenderFundInvestors, setLenderFundInvestors] = useState([]);
  const [dockDealMembers, setDockDealMembers] = useState([]);
  const [cedantSharesForDock, setCedantSharesForDock] = useState([]);
  const [fabLastSeenMs, setFabLastSeenMs] = useState(0);
  const wasOpenRef = useRef(false);
  /** clients/{clientId}.uid when it differs from brokerLinks.brokerUid (legacy bad data) */
  const [brokerUidByClientId, setBrokerUidByClientId] = useState({});
  const [brokerNameByUid, setBrokerNameByUid] = useState({});

  useEffect(() => {
    if (!open) return undefined;
    announceDockOpen('chat');
    const onOtherDock = (e) => {
      if (e.detail?.id && e.detail.id !== 'chat') onOpenChange(false);
    };
    window.addEventListener(FLOATING_DOCK_EVENT, onOtherDock);
    return () => window.removeEventListener(FLOATING_DOCK_EVENT, onOtherDock);
  }, [open, onOpenChange]);

  const isOwnerOnDashboard =
    currentStep === 'dashboard' &&
    currentUser?.uid &&
    currentProperty?.uid === currentUser.uid &&
    !!currentProperty?.id;

  const linkedBrokersKey = useMemo(
    () =>
      (linkedBrokers || [])
        .map((l) => `${l.clientId || ''}:${l.brokerUid || ''}:${l.brokerDisplayName || ''}`)
        .join('|'),
    [linkedBrokers],
  );

  useEffect(() => {
    const links = linkedBrokers || [];
    if (!links.length || !currentUser?.uid) return undefined;
    let cancelled = false;
    (async () => {
      const uidFromClient = {};
      const names = {};
      for (const link of links) {
        if (!link.clientId) continue;
        try {
          const cs = await getDoc(doc(firestore, 'clients', link.clientId));
          if (!cs.exists()) continue;
          const bu = cs.data()?.uid;
          if (bu && typeof bu === 'string') {
            uidFromClient[link.clientId] = bu;
            if (link.brokerUid && bu !== link.brokerUid) {
              addBrokerLink(currentUser.uid, bu, link.clientId, link.brokerDisplayName || '').catch(() => {});
            }
          }
        } catch (_) {
          /* ignore */
        }
      }
      if (cancelled) return;
      if (Object.keys(uidFromClient).length) {
        setBrokerUidByClientId((prev) => ({ ...prev, ...uidFromClient }));
      }
      const uidsToFetch = new Set();
      links.forEach((link) => {
        if (String(link.brokerDisplayName || '').trim()) return;
        const u = uidFromClient[link.clientId] || link.brokerUid;
        if (u) uidsToFetch.add(u);
      });
      await Promise.all(
        [...uidsToFetch].map(async (uid) => {
          try {
            const n = (await getBrokerDisplayName(uid)).trim();
            if (n) names[uid] = n;
          } catch (_) {
            /* ignore */
          }
        }),
      );
      if (!cancelled && Object.keys(names).length) {
        setBrokerNameByUid((prev) => ({ ...prev, ...names }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkedBrokersKey, currentUser?.uid, fundInvestorsAsInvestor]);

  const directChatUser = useMemo(() => {
    if (!currentUser) return null;
    return {
      ...currentUser,
      displayName: userProfile?.displayName || currentUser.displayName,
    };
  }, [currentUser, userProfile]);

  const brokerPersona = isBrokerPersona(userProfile?.persona);
  const isPreteurPrive = userProfile?.persona === 'preteur_prive';

  useEffect(() => {
    if (!open) {
      setView('hub');
      setThread(null);
    }
  }, [open]);

  const handleDockEscape = useCallback(() => {
    if (view === 'thread') {
      setView('hub');
      setThread(null);
    } else {
      onOpenChange(false);
    }
  }, [view, onOpenChange]);

  useRegisterTopEscapeClose(handleDockEscape, open);

  useEffect(() => {
    if (!currentUser?.uid) {
      setFabLastSeenMs(0);
      return;
    }
    const raw = localStorage.getItem(fabLastSeenStorageKey(currentUser.uid));
    const n = raw ? parseInt(raw, 10) : 0;
    setFabLastSeenMs(Number.isFinite(n) ? n : 0);
  }, [currentUser?.uid]);

  /** Mark inbox as seen only when closing the dock (not when opening), so unread styling stays visible in the hub. */
  useEffect(() => {
    if (!currentUser?.uid) return;
    if (wasOpenRef.current && !open) {
      const now = Date.now();
      localStorage.setItem(fabLastSeenStorageKey(currentUser.uid), String(now));
      setFabLastSeenMs(now);
    }
    wasOpenRef.current = open;
  }, [open, currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setConvRows([]);
      return undefined;
    }
    setConvErr(false);
    return subscribeDirectConversationsForUser(
      currentUser.uid,
      (list) => setConvRows(list),
      () => setConvErr(true),
      DIRECT_CONVERSATIONS_INBOX_LIMIT,
    );
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!open || !isPreteurPrive || !currentUser?.uid) {
      setLenderFundInvestors([]);
      return undefined;
    }
    return subscribeFundInvestorsByLender(currentUser.uid, setLenderFundInvestors);
  }, [open, isPreteurPrive, currentUser?.uid]);

  useEffect(() => {
    if (!open || !isOwnerOnDashboard || !currentUser?.uid || !currentProperty?.id) {
      setDockDealMembers([]);
      setCedantSharesForDock([]);
      return undefined;
    }
    let cancelled = false;
    let unsubParticipants = () => {};
    getSharesByProperty(currentUser.uid, currentProperty.id)
      .then((shares) => {
        if (cancelled) return;
        const list = shares || [];
        setCedantSharesForDock(list);
        const tokens = list.map((s) => s.id).filter(Boolean);
        unsubParticipants = subscribeAggregatedShareParticipants(
          tokens,
          setDockDealMembers,
          () => setDockDealMembers([]),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCedantSharesForDock([]);
          setDockDealMembers([]);
        }
      });
    return () => {
      cancelled = true;
      unsubParticipants();
    };
  }, [open, isOwnerOnDashboard, currentUser?.uid, currentProperty?.id]);

  const openThread = useCallback((payload) => {
    setThread(payload);
    setView('thread');
  }, []);

  const hubPeerUid = useCallback(
    (row) => (row.participantUids || []).find((u) => u !== currentUser?.uid) || '',
    [currentUser?.uid],
  );

  const peerHasUnread = useCallback(
    (peerUid) => {
      if (!peerUid || !currentUser?.uid) return false;
      return convRows.some(
        (row) =>
          hubPeerUid(row) === peerUid &&
          directConversationUnreadFromPeer(row, currentUser.uid, fabLastSeenMs),
      );
    },
    [convRows, currentUser?.uid, fabLastSeenMs, hubPeerUid],
  );

  /** Names we can derive without extra Firestore reads (clients, links, fund rows, deal members, brokerDisplayNames cache). */
  const intrinsicPeerNames = useMemo(() => {
    const m = { ...brokerNameByUid };
    (linkedBrokers || []).forEach((link) => {
      const bu = brokerUidByClientId[link.clientId] || link.brokerUid;
      if (!bu) return;
      const n = String(link.brokerDisplayName || '').trim();
      if (n) m[bu] = n;
    });
    if (brokerPersona) {
      (clients || []).forEach((c) => {
        if (c.clientUserId) {
          const n = (c.name || c.email || '').trim();
          if (n) m[c.clientUserId] = n;
        }
      });
    }
    if (isPreteurPrive) {
      (lenderFundInvestors || []).forEach((item) => {
        if (item.lenderUid === currentUser?.uid && item.investorUserId) {
          const n = (item.displayName || item.email || '').trim();
          if (n) m[item.investorUserId] = n;
        }
      });
    }
    // fundInvestors.displayName / email = the investor on the placement, not the lender — never map those to lenderUid.
    (dockDealMembers || []).forEach((p) => {
      if (p.uid && String(p.displayName || '').trim()) {
        m[p.uid] = String(p.displayName).trim();
      }
    });
    return m;
  }, [
    brokerNameByUid,
    linkedBrokers,
    brokerUidByClientId,
    brokerPersona,
    clients,
    isPreteurPrive,
    lenderFundInvestors,
    currentUser?.uid,
    dockDealMembers,
  ]);

  /** brokerDisplayNames fetch for peer UIDs not covered above (e.g. broker seen only via recent). */
  const [fetchedPeerNames, setFetchedPeerNames] = useState({});
  /** displayName from shares/{token}/participants (cédant only) — plus fiable que brokerDisplayNames pour les invités. */
  const [shareParticipantLabels, setShareParticipantLabels] = useState({});

  useEffect(() => {
    setFetchedPeerNames({});
    setShareParticipantLabels({});
  }, [currentUser?.uid]);

  const conversationIdsKey = useMemo(() => (convRows || []).map((r) => r.id).join('|'), [convRows]);

  useEffect(() => {
    if (!open || !currentUser?.uid || !convRows.length) return undefined;
    let cancelled = false;
    (async () => {
      const tokens = new Set();
      for (const row of convRows) {
        const tok = parseShareTokenFromShareRecipientConversationId(row.id);
        if (tok) tokens.add(tok);
      }
      const updates = {};
      for (const tok of tokens) {
        try {
          const sh = await getShare(tok);
          if (!sh?.uid || sh.uid !== currentUser.uid) continue;
          const snap = await getDocs(collection(firestore, 'shares', tok, 'participants'));
          snap.forEach((d) => {
            const n = String(d.data()?.displayName || '').trim();
            if (n) updates[d.id] = n;
          });
        } catch {
          /* ignore */
        }
      }
      if (!cancelled && Object.keys(updates).length) {
        setShareParticipantLabels((prev) => ({ ...prev, ...updates }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, currentUser?.uid, conversationIdsKey]);

  useEffect(() => {
    if (!currentUser?.uid || !convRows.length) return undefined;
    const peerUids = [
      ...new Set(convRows.map((row) => hubPeerUid(row)).filter(Boolean)),
    ];
    const missing = peerUids.filter(
      (uid) =>
        intrinsicPeerNames[uid] == null
        && shareParticipantLabels[uid] == null
        && fetchedPeerNames[uid] === undefined,
    );
    if (missing.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      const updates = {};
      await Promise.all(
        missing.map(async (uid) => {
          try {
            const n = (await getBrokerDisplayName(uid)).trim();
            updates[uid] = n || '';
          } catch (_) {
            updates[uid] = '';
          }
        }),
      );
      if (!cancelled && Object.keys(updates).length) {
        setFetchedPeerNames((prev) => ({ ...prev, ...updates }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    currentUser?.uid,
    convRows,
    hubPeerUid,
    intrinsicPeerNames,
    shareParticipantLabels,
    fetchedPeerNames,
  ]);

  const peerDisplayLabel = useCallback(
    (uid) => {
      if (!uid) return t('directChat.peerFallback');
      const fromShare = shareParticipantLabels[uid];
      if (fromShare) return fromShare;
      const intrinsic = intrinsicPeerNames[uid];
      if (intrinsic) return intrinsic;
      const fetched = fetchedPeerNames[uid];
      if (fetched !== undefined) return fetched || peerLabelFromUid(uid, t);
      return peerLabelFromUid(uid, t);
    },
    [intrinsicPeerNames, shareParticipantLabels, fetchedPeerNames, t],
  );

  /** Same Firebase user can be linked as "courtier" and as prêteur (placement) — show them only under placements. */
  const lenderUidSetFromPlacements = useMemo(() => {
    const s = new Set();
    (fundInvestorsAsInvestor || []).forEach((fi) => {
      if (fi.lenderUid) s.add(fi.lenderUid);
    });
    return s;
  }, [fundInvestorsAsInvestor]);

  /** Linked brokers still shown in "Brokers & clients" (excludes UIDs covered by private lender rows). */
  const displayedLinkedBrokerUids = useMemo(() => {
    const s = new Set();
    (linkedBrokers || []).forEach((link) => {
      const bu = brokerUidByClientId[link.clientId] || link.brokerUid;
      if (bu && !lenderUidSetFromPlacements.has(bu)) s.add(bu);
    });
    return s;
  }, [linkedBrokers, brokerUidByClientId, lenderUidSetFromPlacements]);

  /** brokerDisplayName stored on investor's broker link (may be set even when brokerDisplayNames/{uid} is empty). */
  const linkedBrokerDisplayNameByUid = useMemo(() => {
    const m = {};
    (linkedBrokers || []).forEach((link) => {
      const bu = brokerUidByClientId[link.clientId] || link.brokerUid;
      if (!bu) return;
      const n = String(link.brokerDisplayName || '').trim();
      if (n) m[bu] = n;
    });
    return m;
  }, [linkedBrokers, brokerUidByClientId]);

  const clientUserIdsForBrokerHub = useMemo(() => {
    if (!brokerPersona) return new Set();
    const s = new Set();
    (clients || []).forEach((c) => {
      if (c.clientUserId) s.add(c.clientUserId);
    });
    return s;
  }, [brokerPersona, clients]);

  /** One row per peer UID: keep the conversation with the latest activity. */
  const recentRows = useMemo(() => {
    if (!currentUser?.uid || !convRows.length) return convRows;
    const byPeer = new Map();
    for (const row of convRows) {
      const uid = hubPeerUid(row);
      if (!uid) continue;
      const ts = directMessageTsToMs(row.lastMessageAt);
      const prev = byPeer.get(uid);
      if (!prev || ts >= directMessageTsToMs(prev.lastMessageAt)) {
        byPeer.set(uid, row);
      }
    }
    return [...byPeer.values()].sort(
      (a, b) => directMessageTsToMs(b.lastMessageAt) - directMessageTsToMs(a.lastMessageAt),
    );
  }, [convRows, currentUser?.uid, hubPeerUid]);

  const unreadDirectCount = useMemo(() => {
    if (!currentUser?.uid) return 0;
    const peerUids = [
      ...new Set(convRows.map((row) => hubPeerUid(row)).filter(Boolean)),
    ];
    return peerUids.filter((peer) => peerHasUnread(peer)).length;
  }, [convRows, currentUser?.uid, hubPeerUid, peerHasUnread]);

  const fabBadgeCount = open ? 0 : unreadDirectCount;

  const brokerRows = useMemo(() => {
    const out = [];
    (linkedBrokers || []).forEach((link) => {
      if (!link.clientId || !link.brokerUid || !currentUser?.uid) return;
      const brokerUid = brokerUidByClientId[link.clientId] || link.brokerUid;
      if (lenderUidSetFromPlacements.has(brokerUid)) return;
      const spec = buildBrokerClientConversationSpec(link.clientId, brokerUid, currentUser.uid);
      const label =
        String(link.brokerDisplayName || '').trim()
        || brokerNameByUid[brokerUid]
        || peerLabelFromUid(brokerUid, t);
      out.push({
        key: `broke-${link.clientId}`,
        label,
        unreadPeerUid: brokerUid,
        onSelect: () =>
          openThread({
            conversationId: spec.conversationId,
            ensureSpec: {
              participantUids: spec.participantUids,
              kind: spec.kind,
              context: spec.context,
            },
            title: label,
          }),
      });
    });
    if (brokerPersona) {
      (clients || []).forEach((c) => {
        if (!c.clientUserId || !currentUser?.uid) return;
        const spec = buildBrokerClientConversationSpec(c.id, currentUser.uid, c.clientUserId);
        out.push({
          key: `client-${c.id}`,
          label: c.name || c.email || c.id,
          unreadPeerUid: c.clientUserId,
          onSelect: () =>
            openThread({
              conversationId: spec.conversationId,
              ensureSpec: {
                participantUids: spec.participantUids,
                kind: spec.kind,
                context: spec.context,
              },
              title: c.name || c.email || peerLabelFromUid(c.clientUserId, t),
            }),
        });
      });
    }
    return out;
  }, [
    linkedBrokers,
    brokerUidByClientId,
    brokerNameByUid,
    currentUser?.uid,
    brokerPersona,
    clients,
    openThread,
    t,
    lenderUidSetFromPlacements,
  ]);

  const lenderRows = useMemo(() => {
    const out = [];
    (fundInvestorsAsInvestor || []).forEach((fi) => {
      if (!fi.investorUserId || !fi.lenderUid || fi.id == null) return;
      const spec = buildLenderFundInvestorConversationSpec(fi.id, fi.lenderUid, fi.investorUserId);
      const lenderLabel =
        brokerNameByUid[fi.lenderUid]
        || linkedBrokerDisplayNameByUid[fi.lenderUid]
        || peerLabelFromUid(fi.lenderUid, t);
      out.push({
        key: `inv-fi-${fi.id}`,
        label: lenderLabel,
        unreadPeerUid: fi.lenderUid,
        onSelect: () =>
          openThread({
            conversationId: spec.conversationId,
            ensureSpec: {
              participantUids: spec.participantUids,
              kind: spec.kind,
              context: spec.context,
            },
            title: lenderLabel,
          }),
      });
    });
    if (isPreteurPrive) {
      (lenderFundInvestors || []).forEach((item) => {
        if (!item.investorUserId || !item.lenderUid || item.id == null) return;
        if (item.lenderUid !== currentUser?.uid) return;
        const spec = buildLenderFundInvestorConversationSpec(item.id, item.lenderUid, item.investorUserId);
        out.push({
          key: `lend-fi-${item.id}`,
          label: item.displayName || item.email || peerLabelFromUid(item.investorUserId, t),
          unreadPeerUid: item.investorUserId,
          onSelect: () =>
            openThread({
              conversationId: spec.conversationId,
              ensureSpec: {
                participantUids: spec.participantUids,
                kind: spec.kind,
                context: spec.context,
              },
              title: item.displayName || item.email || peerLabelFromUid(item.investorUserId, t),
            }),
        });
      });
    }
    return out;
  }, [
    fundInvestorsAsInvestor,
    lenderFundInvestors,
    isPreteurPrive,
    currentUser?.uid,
    openThread,
    t,
    brokerNameByUid,
    linkedBrokerDisplayNameByUid,
  ]);

  const dealShareRows = useMemo(() => {
    if (!isOwnerOnDashboard || !currentUser?.uid) return [];
    const ownerShareIds = new Set(cedantSharesForDock.map((s) => s.id).filter(Boolean));
    const shareIdsOrdered = cedantSharesForDock.map((s) => s.id).filter(Boolean);
    return (dockDealMembers || [])
      .filter((p) => p.uid !== currentUser.uid)
      .map((p) => {
        if (
          lenderUidSetFromPlacements.has(p.uid)
          || displayedLinkedBrokerUids.has(p.uid)
          || clientUserIdsForBrokerHub.has(p.uid)
        ) {
          return null;
        }
        const rowTokens = Array.isArray(p.shareTokens) ? p.shareTokens : [];
        const candidates = [
          ...rowTokens.filter((tok) => ownerShareIds.has(tok)),
          ...shareIdsOrdered,
          ...rowTokens,
        ];
        if (!shareIdsOrdered.length && !rowTokens.length) return null;
        const label = p.displayName || peerLabelFromUid(p.uid, t);
        return {
          key: `deal-${p.uid}`,
          label,
          unreadPeerUid: p.uid,
          onSelect: async () => {
            const dmToken = await findShareTokenForDirectChat(currentUser.uid, p.uid, candidates);
            if (!dmToken) {
              window.alert(t('directChat.noResolvableShareToken'));
              return;
            }
            const spec = buildShareRecipientConversationSpec(dmToken, currentUser.uid, p.uid);
            openThread({
              conversationId: spec.conversationId,
              ensureSpec: {
                participantUids: spec.participantUids,
                kind: spec.kind,
                context: spec.context,
              },
              title: label,
              directConvMeta: { kind: spec.kind, context: spec.context },
            });
          },
        };
      })
      .filter(Boolean);
  }, [
    isOwnerOnDashboard,
    dockDealMembers,
    cedantSharesForDock,
    currentUser?.uid,
    openThread,
    t,
    lenderUidSetFromPlacements,
    displayedLinkedBrokerUids,
    clientUserIdsForBrokerHub,
  ]);

  if (!currentUser) return null;

  const dock = (
    <>
      {open && (
        <div
          className={`fixed ${DOCK_PANEL_BOTTOM} ${DOCK_PANEL_RIGHT} z-[200] ${FLOATING_DOCK_CLASS} pointer-events-auto ${DOCK_PANEL_WIDTH} ${DOCK_PANEL_HEIGHT} bg-white border border-slate-200 rounded-xl shadow-2xl flex flex-col overflow-hidden`}
          role="dialog"
          aria-label={t('floatingChat.panelTitle')}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50 shrink-0">
            <span className="text-sm font-semibold text-slate-900 truncate pr-2">{t('floatingChat.panelTitle')}</span>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200 shrink-0"
              aria-label={t('cancel')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {view === 'thread' && thread && directChatUser ? (
            <ThreadPane
              thread={thread}
              currentUser={directChatUser}
              onBack={() => {
                setView('hub');
                setThread(null);
              }}
              t={t}
              currentStep={currentStep}
              currentProperty={currentProperty}
              onOpenDealFromChat={onOpenDealFromChat}
            />
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto p-2.5 space-y-3 bg-slate-100/40">
              <section
                className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden"
                aria-label={t('floatingChat.sectionRecent')}
              >
                <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                    {t('floatingChat.sectionRecent')}
                  </h3>
                </div>
                <div className="p-1.5">
                {convErr ? (
                  <p className="text-xs text-red-600 px-2 py-2">{t('directChat.loadError')}</p>
                ) : recentRows.length === 0 ? (
                  <p className="text-xs text-slate-500 px-2 py-2">{t('directChat.inboxEmpty')}</p>
                ) : (
                  <ul className="space-y-0.5">
                    {recentRows.map((row) => {
                      const uid = hubPeerUid(row);
                      const peerLabel = peerDisplayLabel(uid);
                      const preview = typeof row.lastMessagePreview === 'string' ? row.lastMessagePreview : '';
                      const unread = uid ? peerHasUnread(uid) : false;
                      return (
                        <li key={row.id}>
                          <button
                            type="button"
                            onClick={() => {
                              const explicit =
                                row.kind
                                  ? {
                                      kind: row.kind,
                                      context:
                                        row.context && typeof row.context === 'object' ? row.context : {},
                                    }
                                  : null;
                              openThread({
                                conversationId: row.id,
                                ensureSpec: null,
                                title: peerLabel,
                                directConvMeta: explicit,
                              });
                            }}
                            className="w-full text-left px-2 py-2 rounded-lg hover:bg-slate-50 text-sm"
                          >
                            <span
                              className={`block truncate ${unread ? 'font-bold text-slate-900' : 'font-medium text-slate-800'}`}
                            >
                              {peerLabel}
                            </span>
                            {preview ? (
                              <span
                                className={`line-clamp-1 ${unread ? 'text-xs font-semibold text-slate-700' : 'text-xs text-slate-500'}`}
                              >
                                {preview}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                </div>
              </section>

              <section
                className="rounded-lg border border-indigo-200 bg-white shadow-sm overflow-hidden"
                aria-label={t('floatingChat.sectionBrokers')}
              >
                <div className="px-3 py-2 border-b border-indigo-200/90 bg-indigo-50">
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-indigo-900">
                    {t('floatingChat.sectionBrokers')}
                  </h3>
                </div>
                <div className="p-1.5">
                {brokerRows.length === 0 ? (
                  <p className="text-xs text-slate-500 px-2 py-2">{t('floatingChat.emptyBrokers')}</p>
                ) : (
                  <ul className="space-y-0.5">
                    {brokerRows.map((r) => {
                      const unread = r.unreadPeerUid ? peerHasUnread(r.unreadPeerUid) : false;
                      return (
                        <li key={r.key}>
                          <button
                            type="button"
                            onClick={r.onSelect}
                            className={`w-full text-left px-2 py-2 rounded-lg hover:bg-indigo-50 text-sm ${unread ? 'font-bold text-slate-900' : 'text-slate-800'}`}
                          >
                            {r.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                </div>
              </section>

              <section
                className="rounded-lg border border-emerald-200 bg-white shadow-sm overflow-hidden"
                aria-label={t('floatingChat.sectionPrivateLenders')}
              >
                <div className="px-3 py-2 border-b border-emerald-200/90 bg-emerald-50">
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-emerald-900">
                    {t('floatingChat.sectionPrivateLenders')}
                  </h3>
                </div>
                <div className="p-1.5">
                {lenderRows.length === 0 ? (
                  <p className="text-xs text-slate-500 px-2 py-2">{t('floatingChat.emptyPrivateLenders')}</p>
                ) : (
                  <ul className="space-y-0.5">
                    {lenderRows.map((r) => {
                      const unread = r.unreadPeerUid ? peerHasUnread(r.unreadPeerUid) : false;
                      return (
                        <li key={r.key}>
                          <button
                            type="button"
                            onClick={r.onSelect}
                            className={`w-full text-left px-2 py-2 rounded-lg hover:bg-emerald-50 text-sm ${unread ? 'font-bold text-slate-900' : 'text-slate-800'}`}
                          >
                            {r.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                </div>
              </section>

              <section
                className="rounded-lg border border-violet-200 bg-white shadow-sm overflow-hidden"
                aria-label={t('floatingChat.sectionDealShares')}
              >
                <div className="px-3 py-2 border-b border-violet-200/90 bg-violet-50">
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-violet-900">
                    {t('floatingChat.sectionDealShares')}
                  </h3>
                </div>
                <div className="p-1.5">
                {!isOwnerOnDashboard ? (
                  <p className="text-xs text-slate-500 px-2 py-2">{t('floatingChat.dealSharesHint')}</p>
                ) : dealShareRows.length === 0 ? (
                  <p className="text-xs text-slate-500 px-2 py-2">{t('floatingChat.emptyDealShares')}</p>
                ) : (
                  <ul className="space-y-0.5">
                    {dealShareRows.map((r) => {
                      const unread = r.unreadPeerUid ? peerHasUnread(r.unreadPeerUid) : false;
                      return (
                        <li key={r.key}>
                          <button
                            type="button"
                            onClick={r.onSelect}
                            className={`w-full text-left px-2 py-2 rounded-lg hover:bg-violet-50 text-sm ${unread ? 'font-bold text-slate-900' : 'text-slate-800'}`}
                          >
                            {r.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                </div>
              </section>
            </div>
          )}
        </div>
      )}
      <div className={`fixed ${DOCK_BOTTOM} ${DOCK_RIGHT.chat} z-[200] ${FLOATING_DOCK_CLASS} pointer-events-none`}>
        <button
          type="button"
          onClick={() => onOpenChange(!open)}
          className={`pointer-events-auto relative ${DOCK_FAB_BUTTON} rounded-full bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-indigo-400`}
          aria-expanded={open}
          aria-label={
            fabBadgeCount > 0
              ? `${t('floatingChat.toggle')} (${fabBadgeCount})`
              : t('floatingChat.toggle')
          }
        >
          <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7" />
          {fabBadgeCount > 0 ? (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center border-2 border-white"
              aria-hidden
            >
              {fabBadgeCount > 9 ? '9+' : fabBadgeCount}
            </span>
          ) : null}
        </button>
      </div>
    </>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(dock, document.body);
  }
  return dock;
}
